import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, Loading, type BadgeTone } from '../ui/Page';
import { Avatar } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { listProjects, listTasks, setTaskStatus, type TaskRow, type TaskStatus } from './api';

/**
 * The board (Jira's columns, not its ceremony).
 *
 * A column per status, a card per task, dragged between them. The drag is the fast way; the arrows
 * on each card are the other way, because a board that can only be operated with a mouse is a board
 * half the people who need it cannot use — and on a phone there is no drag at all.
 *
 * Moving a card sends the row's version. Two people dragging the same task must not silently
 * overwrite each other: the server refuses the stale one and the card goes back where it was, which
 * is the honest outcome (context.md section 199).
 */

const COLUMNS: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE'];

const PRIORITY_TONE: Record<string, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'danger',
};

export function TaskBoard({
  projectId,
  assigneeId,
  sprintId = '',
}: {
  projectId: string;
  assigneeId: string;
  /** A sprint's id, `none` for the backlog, or empty for every item (Phase 36). */
  sprintId?: string;
}): React.JSX.Element {
  const client = useQueryClient();

  const [dragging, setDragging] = useState<TaskRow | null>(null);
  const [over, setOver] = useState<TaskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tasks = useQuery({
    queryKey: ['tasks', projectId, assigneeId, sprintId, 'board'],
    queryFn: () =>
      listTasks({
        ...(projectId ? { projectId } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(sprintId ? { sprintId } : {}),
      }),
  });

  const { options: people } = useMembers();

  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  /*
   * `SCCD-6`, when the project has a key and the item has a number.
   *
   * Built here rather than sent by the server because the project is already on the screen and a
   * key is two fields joined by a hyphen — denormalising it onto every task row would be a second
   * copy to keep honest when somebody renames the key.
   */
  const keyOf = (task: TaskRow): string | null => {
    if (task.number === null || task.project_id === null) return null;

    const project = projects.data?.projects.find((entry) => entry.id === task.project_id);

    return project?.key ? `${project.key}-${task.number}` : null;
  };

  const move = useMutation({
    mutationFn: (input: { task: TaskRow; next: TaskStatus }) =>
      setTaskStatus(input.task.id, { status: input.next, expectedVersion: input.task.version }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const byStatus = useMemo(() => {
    const grouped = new Map<TaskStatus, TaskRow[]>(COLUMNS.map((status) => [status, []]));

    for (const task of tasks.data?.tasks ?? []) {
      // A cancelled task is not a column: it is a task that stopped existing for planning purposes,
      // and a fifth column of them would be the widest thing on the board.
      grouped.get(task.status)?.push(task);
    }

    // Overdue first, then by due date, then by priority: the order somebody would work them in.
    for (const list of grouped.values()) list.sort(byUrgency);

    return grouped;
  }, [tasks.data]);

  const member = (id: string | null) =>
    id ? people.find((person) => person.id === id) : undefined;

  const memberName = (id: string | null): string | null =>
    id === null ? null : (member(id)?.name ?? null);

  if (tasks.isLoading) return <Loading />;

  return (
    <>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="board">
        {COLUMNS.map((status) => {
          const list = byStatus.get(status) ?? [];

          return (
            <section
              key={status}
              className={`board__column board__column--${status.toLowerCase()}${
                over === status ? ' board__column--over' : ''
              }`}
              onDragOver={(event) => {
                if (!dragging) return;

                event.preventDefault();
                setOver(status);
              }}
              onDragLeave={() => setOver((current) => (current === status ? null : current))}
              onDrop={(event) => {
                event.preventDefault();
                setOver(null);

                if (dragging && dragging.status !== status) {
                  move.mutate({ task: dragging, next: status });
                }

                setDragging(null);
              }}
            >
              <header className="board__header">
                <h2>{t(`work.status.${status}`)}</h2>
                <span className="board__count">{list.length}</span>
              </header>

              <ul className="board__cards">
                {list.map((task) => (
                  <li
                    key={task.id}
                    className={`board__card${isOverdue(task) ? ' board__card--overdue' : ''}`}
                    draggable
                    onDragStart={() => setDragging(task)}
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                  >
                    <Link to={`/trabalho/tarefas/${task.id}`} className="board__link">
                      {task.title}
                    </Link>

                    <span className="cell-inline board__meta">
                      {/* The key and the estimate: what a team says out loud in a stand-up. */}
                      {keyOf(task) && <code className="backlog__key">{keyOf(task)}</code>}

                      <Badge tone={PRIORITY_TONE[task.priority] ?? 'neutral'}>
                        {t(`work.priority.${task.priority}`)}
                      </Badge>

                      {task.story_points !== null && (
                        <span className="points" title={t('sprint.points')}>
                          {task.story_points}
                        </span>
                      )}

                      {task.due_date && (
                        <span className={isOverdue(task) ? 'lead-idle' : 'muted small'}>
                          {formatDate(task.due_date)}
                        </span>
                      )}

                      {memberName(task.assignee_id) && (
                        <Avatar
                          name={memberName(task.assignee_id)!}
                          url={member(task.assignee_id)?.avatarUrl}
                          size="sm"
                        />
                      )}
                    </span>

                    {/* The other way to move a card: no mouse, and it works on a phone. */}
                    <span className="board__actions">
                      <button
                        type="button"
                        className="button button--quiet button--compact"
                        disabled={COLUMNS.indexOf(status) === 0 || move.isPending}
                        aria-label={t('work.moveBack')}
                        onClick={() =>
                          move.mutate({ task, next: COLUMNS[COLUMNS.indexOf(status) - 1]! })
                        }
                      >
                        ←
                      </button>

                      <button
                        type="button"
                        className="button button--quiet button--compact"
                        disabled={COLUMNS.indexOf(status) === COLUMNS.length - 1 || move.isPending}
                        aria-label={t('work.moveForward')}
                        onClick={() =>
                          move.mutate({ task, next: COLUMNS[COLUMNS.indexOf(status) + 1]! })
                        }
                      >
                        →
                      </button>
                    </span>
                  </li>
                ))}

                {list.length === 0 && <li className="board__empty">{t('work.columnEmpty')}</li>}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}

/** A task is late when its due date has passed and it is not finished. */
export function isOverdue(task: TaskRow): boolean {
  if (!task.due_date || task.status === 'DONE' || task.status === 'CANCELED') return false;

  return new Date(task.due_date).getTime() < Date.now();
}

const PRIORITY_ORDER: Record<string, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

/**
 * The order somebody would work a column in: late first, then by when it is due, then by priority.
 *
 * A task with no due date sorts last rather than first. It is not urgent — it is undated, and
 * putting it above something due tomorrow would be the board making that decision for somebody.
 */
export function byUrgency(a: TaskRow, b: TaskRow): number {
  const lateA = isOverdue(a) ? 0 : 1;
  const lateB = isOverdue(b) ? 0 : 1;

  if (lateA !== lateB) return lateA - lateB;

  const dueA = a.due_date ? new Date(a.due_date).getTime() : Number.POSITIVE_INFINITY;
  const dueB = b.due_date ? new Date(b.due_date).getTime() : Number.POSITIVE_INFINITY;

  if (dueA !== dueB) return dueA - dueB;

  return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
}
